/**
 * Eyeglasses lens configurator.
 *
 * Step 1 is a single checkbox list: a pinned "Frame only" row plus the products
 * of the Medical Lenses collection, paginated 6 per page. Exactly one row can be
 * selected — a frame takes one lens type, so the checkboxes behave like radios.
 *
 *   Frame only     → native product form (Add to cart + Buy it now)
 *   A lens row     → Continue → step 2 (prescription) → adds frame + lens
 *
 * Clicking the body of a lens row navigates to that lens product page, where it
 * can be bought on its own. Only the checkbox selects.
 */
(function () {

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function esc(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function groupAmount(n, decimals, thousands, decimalSep) {
    var parts = Math.abs(n).toFixed(decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return whole + (parts[1] ? decimalSep + parts[1] : '');
  }

  /**
   * products.json returns prices in currency units (e.g. "4880.00"), NOT cents.
   * Shopify.formatMoney() expects cents, so multiply by 100.
   *
   * That helper is not guaranteed to be loaded on a product page, so fall back to
   * rendering the shop's money_format here — otherwise the price loses its
   * currency and reads as a bare "1800.00".
   */
  function formatPrice(priceString, moneyFormat) {
    var amount = parseFloat(priceString);
    if (isNaN(amount)) return '';

    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(Math.round(amount * 100), moneyFormat || '{{amount}}');
    }

    return (moneyFormat || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case 'amount_no_decimals':
          return groupAmount(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return groupAmount(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return groupAmount(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return groupAmount(amount, 2, "'", '.');
        default:
          return groupAmount(amount, 2, ',', '.');
      }
    });
  }

  function variantPrice(product) {
    var variant = product.variants && product.variants[0];
    var price = variant ? parseFloat(variant.price) : NaN;
    // Products without a usable price sort last rather than jumping to the top
    return isNaN(price) ? Infinity : price;
  }

  function getImageSrc(product) {
    var images = product.images;
    if (!images || !images.length) return '';
    var img = images[0];
    return (typeof img === 'string') ? img : (img.src || img.url || '');
  }

  // ─── Per-instance init ───────────────────────────────────────────────────────

  function initConfigurator(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    var moneyFormat = root.dataset.moneyFormat || '{{amount}}';
    var pageSize    = parseInt(root.dataset.pageSize, 10) || 6;
    // '' on the default locale, '/ar-eg' on Arabic. Without it every lens link
    // and the collection fetch would drop the shopper out of their language.
    var rootUrl     = (root.dataset.rootUrl || '/').replace(/\/+$/, '');
    // Localised copy, rendered from the same locale keys the Liquid uses
    var S = {};
    try { S = JSON.parse(root.dataset.strings || '{}'); } catch (e) { S = {}; }

    var state = {
      choice: 'frame-only',   // 'frame-only' | 'lens' — frame only is the default
      lensVariantId: null,
      lensTitle: null,
      lenses: [],
      page: 0,
      prescriptionMethod: 'later',  // 'later' | 'upload'
      prescriptionFileName: null,
    };

    // DOM refs
    var steps         = root.querySelectorAll('[data-configurator-step]');
    var progressSteps = root.querySelectorAll('[data-progress-step]');
    var progressLine2 = root.querySelector('[data-progress-line-2]');
    var progressStep2 = root.querySelector('[data-progress-step="2"]');
    var rowsWrap      = root.querySelector('[data-lens-rows]');
    var frameRow      = root.querySelector('[data-row-choice="frame-only"]');
    var pager         = root.querySelector('[data-lens-pager]');
    var pageLabel     = root.querySelector('[data-lens-page-label]');
    var prevBtn       = root.querySelector('[data-lens-prev]');
    var nextBtn       = root.querySelector('[data-lens-next]');
    var frameActions  = root.querySelector('[data-frame-only-actions]');
    var lensActions   = root.querySelector('[data-lens-actions]');
    var errorEl       = root.querySelector('[data-configurator-error]');
    var uploadWrap    = root.querySelector('[data-prescription-upload-wrap]');
    var fileInput     = root.querySelector('[data-prescription-file]');
    var fileNameEl    = root.querySelector('[data-prescription-file-name]');

    // ── UI helpers ─────────────────────────────────────────────────────────────

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || '';
      errorEl.hidden = !msg;
    }

    function setStep(num) {
      steps.forEach(function (step) {
        var n = parseInt(step.dataset.configuratorStep, 10);
        step.hidden = (n !== num);
        step.classList.toggle('is-active', n === num);
      });

      progressSteps.forEach(function (ps) {
        var n = parseInt(ps.dataset.progressStep, 10);
        ps.classList.toggle('is-active', n === num);
        ps.classList.toggle('is-done', n < num);
      });

      // Step 2 only exists once a lens is chosen
      var hasLens = (state.choice === 'lens');
      if (progressStep2) progressStep2.hidden = !hasLens;
      if (progressLine2) progressLine2.hidden = !hasLens;

      frameActions.hidden = (num !== 1 || state.choice !== 'frame-only');
      if (lensActions) lensActions.hidden = (num !== 1 || state.choice !== 'lens');
    }

    function getFrameVariantId() {
      // Prefer the live product-form input so variant changes are respected
      var formId = root.dataset.productFormId;
      if (formId) {
        var input = document.querySelector('#' + formId + ' input[name="id"]');
        if (input && input.value) return input.value;
      }
      return root.dataset.frameVariantId || '';
    }

    // ── Selection ──────────────────────────────────────────────────────────────

    /**
     * One selection at a time. `variantId` null means the pinned Frame only row.
     * The frame row lives outside the paginated list, so it is updated directly
     * rather than by re-rendering.
     */
    function select(variantId, title) {
      if (variantId) {
        state.choice = 'lens';
        state.lensVariantId = String(variantId);
        state.lensTitle = title || '';
      } else {
        state.choice = 'frame-only';
        state.lensVariantId = null;
        state.lensTitle = null;
      }

      if (frameRow) {
        var frameOn = (state.choice === 'frame-only');
        frameRow.classList.toggle('is-selected', frameOn);
        var frameCheck = frameRow.querySelector('[data-lens-check]');
        if (frameCheck) frameCheck.checked = frameOn;
      }

      rowsWrap.querySelectorAll('[data-lens-row]').forEach(function (row) {
        var on = (row.dataset.variantId === state.lensVariantId);
        row.classList.toggle('is-selected', on);
        var box = row.querySelector('[data-lens-check]');
        if (box) box.checked = on;
      });

      showError('');
      setStep(1);
    }

    // ── Collection fetch ───────────────────────────────────────────────────────

    function fetchProducts(handle) {
      return fetch(rootUrl + '/collections/' + encodeURIComponent(handle) + '/products.json?limit=250')
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (d) { return d.products || []; });
    }

    // ── Row rendering + pagination ─────────────────────────────────────────────

    function rowHtml(product, variant, available) {
      var imgSrc = getImageSrc(product);
      return '' +
        '<label class="lens-option-row__check">' +
          '<input type="checkbox" data-lens-check' + (available ? '' : ' disabled') + '>' +
          '<span class="lens-option-row__box" aria-hidden="true"></span>' +
          '<span class="visually-hidden">' +
            esc(String(S.selectOption || '').replace('__TITLE__', product.title)) +
          '</span>' +
        '</label>' +
        '<a class="lens-option-row__link" href="' + esc(rootUrl) + '/products/' + esc(product.handle) + '">' +
          '<span class="lens-option-row__media">' +
            (imgSrc
              ? '<img src="' + esc(imgSrc) + '" alt="' + esc(product.title) + '" loading="lazy">'
              : '<span class="lens-option-row__media--empty"></span>') +
          '</span>' +
          '<span class="lens-option-row__body">' +
            '<span class="lens-option-row__title">' + esc(product.title) + '</span>' +
            '<span class="lens-option-row__meta">' +
              (available ? formatPrice(variant.price, moneyFormat) : esc(S.soldOut || '')) +
            '</span>' +
          '</span>' +
        '</a>';
    }

    function renderPage() {
      var total  = state.lenses.length;
      var pages  = Math.max(1, Math.ceil(total / pageSize));
      if (state.page > pages - 1) state.page = pages - 1;
      if (state.page < 0) state.page = 0;

      rowsWrap.innerHTML = '';

      if (!total) {
        rowsWrap.innerHTML = '<p class="lens-configurator__empty">' + esc(S.noOptions || '') + '</p>';
        if (pager) pager.hidden = true;
        return;
      }

      state.lenses
        .slice(state.page * pageSize, (state.page + 1) * pageSize)
        .forEach(function (product) {
          var variant = product.variants && product.variants[0];
          if (!variant) return;
          var available = (variant.available !== false);

          var row = document.createElement('div');
          row.className = 'lens-option-row' + (available ? '' : ' is-unavailable');
          row.dataset.lensRow = '';
          row.dataset.variantId = String(variant.id);
          row.innerHTML = rowHtml(product, variant, available);

          if (available) {
            row.querySelector('[data-lens-check]').addEventListener('change', function (e) {
              // Checkbox selects; it must never follow the row link
              e.stopPropagation();
              if (e.target.checked) select(variant.id, product.title);
              else select(null);
            });
          }

          if (String(variant.id) === state.lensVariantId) {
            row.classList.add('is-selected');
            row.querySelector('[data-lens-check]').checked = true;
          }

          rowsWrap.appendChild(row);
        });

      if (pager) {
        pager.hidden = (pages < 2);
        if (pageLabel) pageLabel.textContent = (state.page + 1) + ' / ' + pages;
        if (prevBtn) prevBtn.disabled = (state.page === 0);
        if (nextBtn) nextBtn.disabled = (state.page >= pages - 1);
      }
    }

    function loadLenses() {
      var handle = root.dataset.lensCollection;
      fetchProducts(handle)
        .then(function (products) {
          // Cheapest first, so the entry-level lens leads the list
          state.lenses = products.slice().sort(function (x, y) {
            return variantPrice(x) - variantPrice(y);
          });
          renderPage();
        })
        .catch(function () {
          rowsWrap.innerHTML =
            '<p class="lens-configurator__empty">' + esc(S.loadError || '') + '</p>';
        });
    }

    // ── Cart add ───────────────────────────────────────────────────────────────

    /**
     * Shopify bulk AJAX cart add (JSON) with sections requested — one atomic
     * request that returns the rendered cart drawer HTML, so no page reload.
     */
    function checkoutUrl() {
      var cart = (window.routes && window.routes.cart_url) || '/cart';
      return cart.replace(/\/cart\/?$/, '/checkout');
    }

    function addToCartWithLens(btn, goToCheckout) {
      if (!state.lensVariantId) {
        showError(S.selectFirst);
        return;
      }

      var frameId = getFrameVariantId();
      if (!frameId) {
        showError(S.frameError);
        return;
      }

      showError('');
      var original = btn ? btn.textContent : '';
      if (btn) {
        btn.disabled = true;
        btn.textContent = S.adding || '';
      }

      var frameTitle = root.dataset.frameTitle || '';

      var lensProperties = {
        'Lens Type': state.lensTitle || 'Medical lens',
        'Frame': frameTitle,
      };

      var frameProperties = {
        'Lens option': 'With lenses',
        'Selected lens': state.lensTitle,
      };

      var uploading = (state.prescriptionMethod === 'upload');
      if (!uploading) {
        lensProperties['Prescription'] = 'Send later';
        frameProperties['Prescription'] = 'Send later';
      }

      var drawer = document.querySelector('cart-drawer');
      var sectionIds = (drawer && typeof drawer.getSectionsToRender === 'function')
        ? drawer.getSectionsToRender().map(function (s) { return s.id; }).join(',')
        : 'cart-drawer,cart-icon-bubble';

      /**
       * Multipart rather than JSON: JSON cannot carry a file, and the prescription
       * has to reach the order as a real upload, not just a filename. Shopify's
       * bulk add accepts items[N][properties][key] with a File value.
       */
      var body = new FormData();

      function appendItem(index, id, properties) {
        body.append('items[' + index + '][id]', String(id));
        body.append('items[' + index + '][quantity]', '1');
        Object.keys(properties).forEach(function (key) {
          if (properties[key] == null || properties[key] === '') return;
          body.append('items[' + index + '][properties][' + key + ']', properties[key]);
        });
      }

      appendItem(0, frameId, frameProperties);
      appendItem(1, state.lensVariantId, lensProperties);

      if (uploading) {
        var chosen = fileInput && fileInput.files && fileInput.files[0];
        if (chosen) {
          body.append('items[0][properties][Prescription]', chosen);
          body.append('items[1][properties][Prescription]', chosen);
        }
      }

      body.append('sections', sectionIds);
      body.append('sections_url', window.location.pathname);

      if (drawer && typeof drawer.setActiveElement === 'function') {
        drawer.setActiveElement(document.activeElement);
      }

      // No Content-Type header: the browser must set the multipart boundary
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: body,
      })
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            throw new Error(response.description || response.message || S.cartError);
          }
          if (goToCheckout) {
            window.location.href = checkoutUrl();
            return;
          }
          openCartDrawer(response);
        })
        .catch(function (err) {
          showError(err.message || S.cartError);
        })
        .finally(function () {
          if (btn) {
            btn.disabled = false;
            btn.textContent = original;
          }
        });
    }

    /**
     * Update the cart drawer exactly like product-form.js does:
     * inject rendered section HTML then open the drawer.
     */
    function openCartDrawer(response) {
      var drawer = document.querySelector('cart-drawer');

      if (!drawer) {
        // No drawer on this theme configuration — fall back to cart page
        window.location.href = window.routes.cart_url;
        return;
      }

      if (response && response.sections && typeof drawer.renderContents === 'function') {
        if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'lens-configurator',
            cartData: response,
          });
        }

        drawer.renderContents(response);
        return;
      }

      // sections not returned (shouldn't happen) — open drawer manually
      if (typeof drawer.open === 'function') drawer.open();
    }

    // ── Event listeners ────────────────────────────────────────────────────────

    // Pinned Frame only row
    if (frameRow) {
      var frameCheckbox = frameRow.querySelector('[data-lens-check]');
      if (frameCheckbox) {
        frameCheckbox.addEventListener('change', function () {
          // Unchecking Frame only with nothing else chosen is meaningless
          select(null);
        });
      }
    }

    // Pagination
    if (prevBtn) {
      prevBtn.addEventListener('click', function () { state.page -= 1; renderPage(); });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () { state.page += 1; renderPage(); });
    }

    // Continue → prescription
    var continueBtn = root.querySelector('[data-lens-continue]');
    if (continueBtn) {
      continueBtn.addEventListener('click', function () {
        if (!state.lensVariantId) {
          showError(S.selectFirst);
          return;
        }
        setStep(2);
      });
    }

    // Back buttons
    root.querySelectorAll('[data-configurator-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var active = root.querySelector('[data-configurator-step]:not([hidden])');
        var num = active ? parseInt(active.dataset.configuratorStep, 10) : 1;
        if (num > 1) setStep(num - 1);
        showError('');
      });
    });

    // Prescription method radio
    root.querySelectorAll('[data-prescription-method]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        state.prescriptionMethod = radio.value;
        if (uploadWrap) uploadWrap.hidden = (radio.value !== 'upload');
      });
    });

    // Prescription file upload
    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        state.prescriptionFileName = file ? file.name : null;
        if (fileNameEl) fileNameEl.textContent = file ? 'Selected: ' + file.name : '';
      });
    }

    // Step 2 Add to cart / Buy it now — same add, different destination
    function step2Submit(btn, goToCheckout) {
      if (state.prescriptionMethod === 'upload' && !state.prescriptionFileName) {
        showError(S.uploadRequired);
        return;
      }
      addToCartWithLens(btn, goToCheckout);
    }

    var step2AddBtn = root.querySelector('[data-step2-add-cart]');
    if (step2AddBtn) {
      step2AddBtn.addEventListener('click', function () { step2Submit(step2AddBtn, false); });
    }

    var step2BuyBtn = root.querySelector('[data-step2-buy-now]');
    if (step2BuyBtn) {
      step2BuyBtn.addEventListener('click', function () { step2Submit(step2BuyBtn, true); });
    }

    // Keep frameVariantId in sync with variant selector
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!event.data || !event.data.variant) return;
        if (event.data.sectionId && event.data.sectionId !== root.dataset.sectionId) return;
        root.dataset.frameVariantId = String(event.data.variant.id);
      });
    }

    loadLenses();
    setStep(1);
  }

  // ─── Bootstrap ──────────────────────────────────────────────────────────────

  function initAll() {
    document.querySelectorAll('[data-lens-configurator]').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);

})();
